// Used in resources-list.component.html, assignables.component.html, assignables-list.component.html

// Angular
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

// Services/Types/Helpers
import { AssetAssociationsHelper } from "#helpers/asset-associations.helper";
import { GetBookshelfUrlHelper } from "#helpers/get-bookshelf-url.helper";
import { GetDiscoverActivityTypeNameHelper } from "#helpers/get-discover-activity-type.helper";
import { LanguageHelper } from "#helpers/language.helper";
import {
    CourseMetrics,
    PPUser as User,
} from "cli-types";

// RXJS/Store
import * as fromCourseStore from "#core/teacher-classes/store";
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as profileStore from "#login/store";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Components
import { ActivityPreviewDialogComponent } from "#components/dialog-boxes/activity-preview-dialog/activity-preview-dialog.component";
import { EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, CustomActivity, Edition, Subject as SubjectType, assetTypeIds, eBookActivity } from "@carnegie-learning/cure-types";

@Component({
    selector: "assignable-expanded-singular",
    templateUrl: "./assignable-expanded-singular.component.html",
    styleUrls: ["./assignable-expanded-singular.component.scss"],
    standalone: false
})
export class SingularAssignableComponent implements OnInit, OnDestroy {
    userProfile: User;
    activityPreviewDialogRef: MatDialogRef<ActivityPreviewDialogComponent> | null;
    readonly assetTypeIds = assetTypeIds;
    dashes = "---";
    private ngUnsubscribe = new Subject();

    @Input() asset: eBookActivity;
    @Input() editions: Edition[];
    @Input() editionId: EditionId;
    @Input() embedded?: boolean;
    subject: SubjectType;

    constructor(
        private getDiscoverActivityTypeNameHelper: GetDiscoverActivityTypeNameHelper,
        private getBookshelfUrl: GetBookshelfUrlHelper,
        private languageHelper: LanguageHelper,
        public dialog: MatDialog,
        public assetAssociationsHelper: AssetAssociationsHelper,
        public profile_store: Store<profileStore.UserState>,
        private courseStore: Store<fromCourseStore.CoursesState>
    ) {}

    ngOnInit() {
        this.profile_store
            .pipe(select(profileStore.getUserProfile))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((userProfile) => {
                if (userProfile) {
                    this.userProfile = userProfile;
                }
            });

        this.courseStore
            .pipe(select(fromCoursesStore.getAllActiveSubjects))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((subjects: SubjectType[]) => {
                if (subjects && subjects.length > 0) {
                    const subject = subjects.find(
                        (s) => s.subjectId === this.asset.subjectId
                    );
                    if (subject) {
                        this.subject = subject;
                    }
                }
            });
    }

    getProgramName() {
        if (
            this.asset &&
            this.asset.editionIds &&
            this.asset.editionIds.length &&
            this.asset.subjectId
        ) {
            const assetEdition =
                this.editions && this.editions.length
                    ? this.editions.find(
                          (edt) => this.editionId === edt.editionId
                      )
                    : undefined;

            let editionName: string | undefined;
            if (assetEdition) {
                editionName = assetEdition.editionName;
            }
            return editionName || this.subject?.subjectName;
        } else {
            return "---";
        }
    }

    getDiscoverActivityTypeName(activityType: string) {
        return this.getDiscoverActivityTypeNameHelper.getDiscoverActivityTypeName(
            activityType
        );
    }

    getSpineImage() {
        return (
            "./assets/images/" +
            (<eBookActivity>this.asset).bookImage +
            "-lg." +
            (<eBookActivity>this.asset).bookImageExt
        );
    }

    getLevel(assetLevelIds: string[]): string {
        if (assetLevelIds && this.subject) {
            return this.assetAssociationsHelper
                .getLevels(this.subject)
                ?.filter((level) =>
                    assetLevelIds.some((id) => id === level.levelId)
                )
                .map((l) => l.levelName)
                .join(", ");
        } else {
            return "---";
        }
    }

    previewActivity(asset: Asset) {
        this.activityPreviewDialogRef = this.dialog.open(
            ActivityPreviewDialogComponent,
            {
                data: {
                    asset: asset,
                    compositeID: null,
                    compositeType: null,
                    userID: this.userProfile.userID,
                },
            }
        );
    }

    getFormatedCourseMetricData(data: CourseMetrics) {
        // TODO: Replace with helper?
        return [
            {
                type: "Pending",
                value: data.pending,
                filter: "pending",
                style: this.getMetricsStyle("pending", data.pending),
                disable: data.pending === 0,
            },
            {
                type: "Due Today",
                value: data.dueToday,
                filter: "dueToday",
                style: this.getMetricsStyle("dueToday", data.dueToday),
                disable: data.dueToday === 0,
            },
            {
                type: "Overdue",
                value: data.overdue,
                filter: "overdue",
                style: this.getMetricsStyle("overdue", data.overdue),
                disable: data.overdue === 0,
            },
            {
                type: "Submitted",
                value: data.submitted,
                filter: "submitted",
                style: this.getMetricsStyle("submitted", data.submitted),
                disable: data.submitted === 0,
            },
            {
                type: "Evaluated",
                value: data.evaluated,
                filter: "evaluated",
                style: this.getMetricsStyle("evaluated", data.evaluated),
                disable: data.evaluated === 0,
            },
            {
                type: "Scheduled",
                value: data.scheduled,
                filter: "scheduled",
                style: this.getMetricsStyle("scheduled", data.scheduled),
                disable: data.scheduled === 0,
            },
        ];
    }

    getMetricsStyle(type: string, value: number) {
        // TODO: Replace with helper?
        switch (type) {
            case "pending":
                return value > 0 ? "bgPending" : "bgPending bgNoValue";
            case "dueToday":
                return value > 0 ? "bgDueToday" : "bgDueToday bgNoValue";
            case "overdue":
                return value > 0 ? "bgOverdue" : "bgOverdue bgNoValue";
            case "submitted":
                return value > 0 ? "bgSubmitted" : "bgSubmitted bgNoValue";
            case "evaluated":
                return value > 0 ? "bgEvaluated" : "bgEvaluated bgNoValue";
            case "scheduled":
                return value > 0 ? "bgScheduled" : "bgScheduled bgNoValue";
            default:
                return null;
        }
    }

    isMath(subjectId: SubjectId) {
        return this.languageHelper.ifMathLanguage(subjectId);
    }

    // Get Activity Number for asset
    getActivityNumber(asset: Asset | undefined): string {
        let activityNumber: string = this.dashes; // if undefined just use the dashes
        if (asset) {
            switch (asset.assetTypeId) {
                case assetTypeIds.CUSTOM_ACT:
                    activityNumber =
                        (<CustomActivity>asset).bookActivityNumber ||
                        activityNumber;
                    break;
                case assetTypeIds.EBOOK_ACT:
                    activityNumber =
                        (<eBookActivity>asset).bookActivityNumber ||
                        activityNumber;
                    break;
            }
        }
        return activityNumber;
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
